import { Trans } from "@lingui/macro";
import { ApiEpBilling, IPeriodUnit } from "@smp/api";
import {
  UiCard,
  UiCardActions,
  UiCardContent,
  UiDialogResponsive,
  UiGrid,
  UiLink,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr, textCapitalize } from "@smp/utils";
import format from "date-fns/format";
import formatDistance from "date-fns/formatDistance";
import parseISO from "date-fns/parseISO";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useDialog } from "@/hooks/use_dialog";
import { useEpBillingProductPlanProductList } from "@/hooks/use_ep_billing_product_plan_product";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useHumanizer } from "@/hooks/use_humanizer";
import { useMePermissions } from "@/hooks/use_me_permissions";
import { DialogPurchase } from "@/screens/app/components/dialogs/dialog_purchase";
import { useContextApp } from "@/screens/app/context/app";

export const DialogPolotno = ({ onClose }: { onClose: () => void }) => {
  const {
    state: {
      me,
      planActive,
      planLast,
      plansubscriptionActive,
      plansubscriptionLast,
    },
  } = useContextApp();
  const mePermissions = useMePermissions();

  const {
    close: dialogPurchaseClose,
    isOpen: isDialogPurchaseOpen,
    open: dialogPurchaseOpen,
  } = useDialog();

  const { productPlanProductList } = useEpBillingProductPlanProductList({
    req: {
      query: {
        billing_policy_id: me.billing_policy_id,
        plan_id: planLast?.id,
      },
    },
    useQueryOptions: { enabled: !!planLast },
  });

  const { failureTranslationGet } = useFailureTranslator();

  const { humanizeBillingPlanId } = useHumanizer();

  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isMutatePurchaseLoading, mutate: mutatePurchase } =
    useMutation(ApiEpBilling.Product.PlanProduct.purchase, {
      onError: (clause) => {
        enqueueSnackbar(failureTranslationGet(clause), { variant: "error" });
      },
    });

  const product =
    productPlanProductList?.find(
      (plan) => plan.period_units === IPeriodUnit.YEAR,
    ) || productPlanProductList?.[0];

  const handlePurchaseFreeForeign = () => {
    if (!product) return;

    mutatePurchase({
      body: {
        allow_internal_funds: false,
        payment_provider: "paypal",
        payment_type: "single",
      },
      path: { id: product.id },
    });
  };

  return (
    <>
      <UiDialogResponsive
        fullWidth
        maxWidth={632}
        onClose={onClose}
        open
        title={<Trans>My Plan</Trans>}
      >
        <UiGrid container spacing={2}>
          <UiGrid item xs={12} sm={plansubscriptionLast ? 6 : 12}>
            <UiCard color="primary" fullHeight fullWidth>
              <UiCardContent>
                <UiTypography gutterBottom>
                  {planLast ? (
                    humanizeBillingPlanId(planLast.id) ||
                    textCapitalize(planLast.name)
                  ) : (
                    <Trans>Not selected</Trans>
                  )}
                </UiTypography>
              </UiCardContent>
              <UiCardActions>
                <UiTypography>
                  <UiLink
                    color="inherit"
                    component={HelperAdapterLink}
                    onClick={onClose}
                    to="/billing/plans"
                    {...testAppendAttr("id", "change_billing_plan")}
                  >
                    <Trans>Change Subscription Plan</Trans>
                  </UiLink>
                </UiTypography>
              </UiCardActions>
            </UiCard>
          </UiGrid>
          {plansubscriptionLast && (
            <UiGrid item xs={12} sm={6}>
              {!!plansubscriptionActive ? (
                <UiCard color="info" fullHeight fullWidth>
                  <UiCardContent>
                    <UiTypography>
                      <Trans>Paid up to</Trans>
                    </UiTypography>
                    <UiTypography>
                      {format(
                        parseISO(plansubscriptionLast.period_end),
                        "d MMMM yyyy",
                      )}{" "}
                      (
                      {formatDistance(
                        parseISO(plansubscriptionLast.period_end),
                        Date.now(),
                      )}
                      )
                    </UiTypography>
                  </UiCardContent>
                  {mePermissions.renew_plan && (
                    <UiCardActions>
                      <UiTypography>
                        <UiLink
                          color="inherit"
                          onClick={dialogPurchaseOpen}
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          <Trans>Renew</Trans>
                        </UiLink>
                      </UiTypography>
                    </UiCardActions>
                  )}
                </UiCard>
              ) : (
                <UiCard color="warning" fullHeight fullWidth>
                  <UiCardContent>
                    <UiTypography>
                      <Trans>Expired</Trans>
                    </UiTypography>
                    <UiTypography>
                      {format(
                        parseISO(plansubscriptionLast?.period_end),
                        "d MMMM yyyy",
                      )}
                    </UiTypography>
                  </UiCardContent>
                  {planLast &&
                    !planLast.is_trial &&
                    !["free", "free_foreign"].includes(planLast.id) && (
                      <UiCardActions>
                        <UiLink
                          color="primary"
                          onClick={dialogPurchaseOpen}
                          variant="subtitle1"
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          <Trans>Renew</Trans>
                        </UiLink>
                      </UiCardActions>
                    )}
                  {planLast &&
                    !planLast.is_trial &&
                    planLast.id === "free_foreign" &&
                    !planActive && (
                      <UiCardActions>
                        <UiLink
                          color="primary"
                          onClick={
                            isMutatePurchaseLoading
                              ? undefined
                              : handlePurchaseFreeForeign
                          }
                          variant="subtitle1"
                          {...testAppendAttr("id", "renew_billing_plan")}
                        >
                          {isMutatePurchaseLoading ? (
                            <Trans>Loading</Trans>
                          ) : (
                            <Trans>Renew</Trans>
                          )}
                        </UiLink>
                      </UiCardActions>
                    )}
                </UiCard>
              )}
            </UiGrid>
          )}
        </UiGrid>
      </UiDialogResponsive>
      {product && isDialogPurchaseOpen && (
        <DialogPurchase onClose={dialogPurchaseClose} open product={product} />
      )}
    </>
  );
};
